import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Card, CardContent, Grid, CircularProgress, Chip, Divider } from '@mui/material';
import { apiService } from '../services/api';

interface AnalyticsData {
  operationalMetrics: {
    totalCustomers: number;
    activeCustomers: number;
    messagesByChannel: { sms: number; email: number; whatsapp: number };
    avgResponseTime: number;
    bookingRate: number;
    escalationRate: number;
  };
  businessIntelligence: {
    customerAcquisitionRate: number;
    revenueByService: Record<string, number>;
    peakBookingHours: string[];
    customerLifetimeValue: number;
    churnRate: number;
  };
  aiAgentInsights: {
    commonIntents: Array<{ intent: string; count: number }>;
    successRate: number;
    avgConversationLength: number;
    failedIntentCount: number;
  };
}

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AnalyticsData | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      // Get real analytics data from API
      const analyticsData = await apiService.getAnalytics();
      // Ensure all nested properties exist
      setData({
        operationalMetrics: {
          totalCustomers: analyticsData?.operationalMetrics?.totalCustomers ?? 0,
          activeCustomers: analyticsData?.operationalMetrics?.activeCustomers ?? 0,
          messagesByChannel: analyticsData?.operationalMetrics?.messagesByChannel ?? { sms: 0, email: 0, whatsapp: 0 },
          avgResponseTime: analyticsData?.operationalMetrics?.avgResponseTime ?? 0,
          bookingRate: analyticsData?.operationalMetrics?.bookingRate ?? 0,
          escalationRate: analyticsData?.operationalMetrics?.escalationRate ?? 0,
        },
        businessIntelligence: {
          customerAcquisitionRate: analyticsData?.businessIntelligence?.customerAcquisitionRate ?? 0,
          revenueByService: analyticsData?.businessIntelligence?.revenueByService ?? {},
          peakBookingHours: analyticsData?.businessIntelligence?.peakBookingHours ?? [],
          customerLifetimeValue: analyticsData?.businessIntelligence?.customerLifetimeValue ?? 0,
          churnRate: analyticsData?.businessIntelligence?.churnRate ?? 0,
        },
        aiAgentInsights: {
          commonIntents: analyticsData?.aiAgentInsights?.commonIntents ?? [],
          successRate: analyticsData?.aiAgentInsights?.successRate ?? 0,
          avgConversationLength: analyticsData?.aiAgentInsights?.avgConversationLength ?? 0,
          failedIntentCount: analyticsData?.aiAgentInsights?.failedIntentCount ?? 0,
        },
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      // On error, show empty/zero data instead of crashing
      setData({
        operationalMetrics: {
          totalCustomers: 0,
          activeCustomers: 0,
          messagesByChannel: { sms: 0, email: 0, whatsapp: 0 },
          avgResponseTime: 0,
          bookingRate: 0,
          escalationRate: 0,
        },
        businessIntelligence: {
          customerAcquisitionRate: 0,
          revenueByService: {},
          peakBookingHours: [],
          customerLifetimeValue: 0,
          churnRate: 0,
        },
        aiAgentInsights: {
          commonIntents: [],
          successRate: 0,
          avgConversationLength: 0,
          failedIntentCount: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // Ensure data is always defined with safe defaults
  const safeData = useMemo(() => {
    if (!data) {
      return {
        operationalMetrics: {
          totalCustomers: 0,
          activeCustomers: 0,
          messagesByChannel: { sms: 0, email: 0, whatsapp: 0 },
          avgResponseTime: 0,
          bookingRate: 0,
          escalationRate: 0,
        },
        businessIntelligence: {
          customerAcquisitionRate: 0,
          revenueByService: {},
          peakBookingHours: [],
          customerLifetimeValue: 0,
          churnRate: 0,
        },
        aiAgentInsights: {
          commonIntents: [],
          successRate: 0,
          avgConversationLength: 0,
          failedIntentCount: 0,
        },
      };
    }
    return {
      operationalMetrics: {
        totalCustomers: data.operationalMetrics?.totalCustomers ?? 0,
        activeCustomers: data.operationalMetrics?.activeCustomers ?? 0,
        messagesByChannel: data.operationalMetrics?.messagesByChannel ?? { sms: 0, email: 0, whatsapp: 0 },
        avgResponseTime: data.operationalMetrics?.avgResponseTime ?? 0,
        bookingRate: data.operationalMetrics?.bookingRate ?? 0,
        escalationRate: data.operationalMetrics?.escalationRate ?? 0,
      },
      businessIntelligence: {
        customerAcquisitionRate: data.businessIntelligence?.customerAcquisitionRate ?? 0,
        revenueByService: data.businessIntelligence?.revenueByService ?? {},
        peakBookingHours: data.businessIntelligence?.peakBookingHours ?? [],
        customerLifetimeValue: data.businessIntelligence?.customerLifetimeValue ?? 0,
        churnRate: data.businessIntelligence?.churnRate ?? 0,
      },
      aiAgentInsights: {
        commonIntents: data.aiAgentInsights?.commonIntents ?? [],
        successRate: data.aiAgentInsights?.successRate ?? 0,
        avgConversationLength: data.aiAgentInsights?.avgConversationLength ?? 0,
        failedIntentCount: data.aiAgentInsights?.failedIntentCount ?? 0,
      },
    };
  }, [data]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Analytics & Reports
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Operational Metrics
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Total Customers:</Typography>
                  <Typography variant="body2" fontWeight="bold">{safeData.operationalMetrics.totalCustomers}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Active Customers:</Typography>
                  <Typography variant="body2" fontWeight="bold">{safeData.operationalMetrics.activeCustomers}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Avg Response Time:</Typography>
                  <Typography variant="body2" fontWeight="bold">{safeData.operationalMetrics.avgResponseTime} min</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Booking Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold">{safeData.operationalMetrics.bookingRate.toFixed(1)}%</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Escalation Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold">{safeData.operationalMetrics.escalationRate}%</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Business Intelligence
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Customer Growth:</Typography>
                  <Typography variant="body2" fontWeight="bold">+{safeData.businessIntelligence.customerAcquisitionRate}%</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Lifetime Value:</Typography>
                  <Typography variant="body2" fontWeight="bold">${safeData.businessIntelligence.customerLifetimeValue}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Churn Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold">{safeData.businessIntelligence.churnRate}%</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" fontWeight="bold" mb={1}>Peak Booking Times:</Typography>
                {safeData.businessIntelligence.peakBookingHours.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {safeData.businessIntelligence.peakBookingHours.map((hour, idx) => (
                      <Chip key={idx} label={hour} size="small" />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No bookings yet</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AI Agent Insights
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Success Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">{safeData.aiAgentInsights.successRate}%</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography variant="body2">Avg Conversation Length:</Typography>
                  <Typography variant="body2" fontWeight="bold">{safeData.aiAgentInsights.avgConversationLength} messages</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" fontWeight="bold" mb={1}>Top Customer Intents:</Typography>
                {safeData.aiAgentInsights.commonIntents.length > 0 ? (
                  safeData.aiAgentInsights.commonIntents.map((item, idx) => (
                    <Box key={idx} display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">{item.intent}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.count} times</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No intents tracked yet</Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

